// DropReceiver — downloads the files of one manifest from one sender, one file at a time.
//
// Resume is driven from here: whenever a file's channel dies (network change, full reconnect)
// the receiver waits for the sender to be authenticated again and asks for the file from its
// contiguous offset. Files land in OPFS under `<key>-<fileId>`, so reloading the page while the
// sender stays online resumes from what is already on disk.
//
// Events: 'change' (throttled progress), 'sender-gone', 'sender-back', 'cancelled'.

import type { Mesh } from '../core/mesh';
import { CHUNK, parseCtl, type FileMeta } from './protocol';
import { openSink, type Sink } from './sinks';
import { receiveInto, type ReceiveState } from './transfer';

const CHANNEL_WAIT_MS = 30_000;
const MAX_INTEGRITY_RETRIES = 3;
const CHANGE_EVERY_MS = 100;

export type IncomingFile = FileMeta & {
  received: number;
  status: 'pending' | 'receiving' | 'done' | 'error';
  file: File | null;
  error?: string;
};

class Retry extends Error {}

export class DropReceiver extends EventTarget {
  readonly files: IncomingFile[];
  readonly totalSize: number;
  #expect: { id: string; resolve: (dc: RTCDataChannel) => void } | null = null;
  #onDown: (() => void) | null = null;
  #cancelled = false;
  #lastChange = 0;
  #listeners: [string, EventListener][] = [];

  constructor(
    readonly mesh: Mesh,
    readonly peer: string,
    readonly from: string,
    metas: FileMeta[],
    readonly key: string,
  ) {
    super();
    this.files = metas.map((m) => ({ ...m, received: 0, status: 'pending', file: null }));
    this.totalSize = metas.reduce((n, f) => n + f.size, 0);
  }

  get received(): number {
    return this.files.reduce((n, f) => n + f.received, 0);
  }

  get complete(): boolean {
    return this.files.every((f) => f.status === 'done');
  }

  start(): void {
    this.#on('channel', ({ peer, channel }) => {
      const dc = channel as RTCDataChannel;
      const [kind, id] = dc.label.split(':');
      if (peer !== this.peer || kind !== 'f' || this.#expect?.id !== id) return;
      this.#expect.resolve(dc);
      this.#expect = null;
    });
    this.#on('down', ({ peer }) => peer === this.peer && this.#onDown?.());
    this.#on('gone', ({ peer }) => {
      if (peer !== this.peer) return;
      this.#onDown?.();
      this.dispatchEvent(new Event('sender-gone'));
    });
    this.#on('authed', ({ peer }) => peer === this.peer && this.dispatchEvent(new Event('sender-back')));
    this.#on('ctl', ({ peer, msg }) => {
      const m = parseCtl(msg);
      if (peer !== this.peer || m?.t !== 'cancel' || m.file || this.complete || this.#cancelled) return;
      this.#cancelled = true;
      this.#onDown?.();
      this.dispatchEvent(new Event('cancelled'));
    });
  }

  get cancelled(): boolean {
    return this.#cancelled;
  }

  async downloadAll(): Promise<void> {
    for (const f of this.files) {
      if (this.#cancelled) return;
      if (f.status !== 'done') await this.#fetch(f);
    }
  }

  cancel(): void {
    this.#cancelled = true;
    this.mesh.send(this.peer, { t: 'cancel' });
    this.#onDown?.();
    this.stop();
  }

  stop(): void {
    for (const [type, fn] of this.#listeners) this.mesh.removeEventListener(type, fn);
    this.#listeners = [];
  }

  #on(type: string, fn: (detail: Record<string, any>) => void): void {
    const listener: EventListener = (e) => fn((e as CustomEvent).detail);
    this.mesh.addEventListener(type, listener);
    this.#listeners.push([type, listener]);
  }

  #changed(force = false): void {
    const now = performance.now();
    if (!force && now - this.#lastChange < CHANGE_EVERY_MS) return;
    this.#lastChange = now;
    this.dispatchEvent(new Event('change'));
  }

  async #fetch(f: IncomingFile): Promise<void> {
    const sink = await openSink(`${this.key}-${f.id}`, { keep: true });
    // In-order writes: what is on disk is a valid prefix. Round down to a chunk boundary anyway.
    const state: ReceiveState = { contiguous: Math.min(f.size, Math.floor(sink.existing / CHUNK) * CHUNK), hashFailures: 0 };
    f.status = 'receiving';
    f.received = state.contiguous;
    this.#changed(true);
    let integrityRetries = 0;

    while (!this.#cancelled) {
      await this.#whenSenderReady();
      if (this.#cancelled) return;
      try {
        await this.#attempt(f, sink, state);
        break;
      } catch (e) {
        if (!(e instanceof Retry)) throw e;
        if (state.hashFailures > integrityRetries && ++integrityRetries > MAX_INTEGRITY_RETRIES) {
          f.status = 'error';
          f.error = 'données corrompues à plusieurs reprises';
          this.#changed(true);
          return;
        }
      }
    }
    if (this.#cancelled) return;
    f.file = await sink.close(f.name, f.type);
    f.status = 'done';
    f.received = f.size;
    this.mesh.send(this.peer, { t: 'done', file: f.id });
    this.#changed(true);
  }

  /** One request/channel cycle. Rejects with Retry when it should be asked again. */
  async #attempt(f: IncomingFile, sink: Sink, state: ReceiveState): Promise<void> {
    let down!: () => void;
    const lost = new Promise<never>((_, reject) => (down = () => reject(new Retry('sender down'))));
    lost.catch(() => {});
    this.#onDown = down;
    try {
      const channel = new Promise<RTCDataChannel>((resolve, reject) => {
        this.#expect = { id: f.id, resolve };
        setTimeout(() => reject(new Retry('no channel')), CHANNEL_WAIT_MS);
      });
      if (!this.mesh.send(this.peer, { t: 'get', file: f.id, offset: state.contiguous })) throw new Retry('not ready');
      const dc = await Promise.race([channel, lost]);
      const rx = receiveInto(dc, sink, f.size, state, (n) => {
        f.received = n;
        this.#changed();
      });
      try {
        await Promise.race([rx.done, lost]);
      } catch (e) {
        await rx.drain().catch(() => {});
        dc.close();
        throw e instanceof Retry ? e : new Retry((e as Error).message);
      }
    } finally {
      this.#expect = null;
      this.#onDown = null;
    }
  }

  #whenSenderReady(): Promise<void> {
    const entry = this.mesh.peers.get(this.peer);
    if (entry?.authed && entry.session.ready) return Promise.resolve();
    return new Promise((resolve) => {
      const check = (e: Event) => {
        const { peer } = (e as CustomEvent).detail;
        if (peer !== this.peer && !this.#cancelled) return;
        this.mesh.removeEventListener('authed', check);
        resolve();
      };
      this.mesh.addEventListener('authed', check);
    });
  }
}
